import { normalizeSizeValue, normalizeSizeVariantsForPublicData } from './sizeHelpers';

const toNonNegativeIntegerOrNull = value => {
  if (value === '' || value == null) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const getPublicData = listing => listing?.attributes?.publicData || listing?.publicData || {};

const getVariantStockBySizeValue = listing => {
  const publicData = getPublicData(listing);
  const variants = normalizeSizeVariantsForPublicData(publicData?.sizes || []);

  return variants.reduce((acc, variant) => {
    const sizeValue = normalizeSizeValue(variant?.sizeOption?.values || variant?.size?.values);
    const stock = toNonNegativeIntegerOrNull(variant?.stock);

    if (!sizeValue || stock == null) {
      return acc;
    }

    const existingStock = acc[sizeValue];
    acc[sizeValue] = Number.isFinite(existingStock) ? Math.max(existingStock, stock) : stock;
    return acc;
  }, {});
};

export const getExplicitListingStockQuantity = listing => {
  const publicData = getPublicData(listing);
  const candidates = [
    listing?.currentStock?.attributes?.quantity,
    listing?.attributes?.quantity,
    listing?.quantity,
    publicData?.availableStock,
    publicData?.stock,
  ];

  for (const candidate of candidates) {
    const parsed = toNonNegativeIntegerOrNull(candidate);
    if (parsed != null) {
      return parsed;
    }
  }

  return null;
};

export const resolvePurchasableStockQuantity = (listing, selectedSize = null) => {
  const normalizedSelectedSize = normalizeSizeValue(selectedSize);
  const stockBySizeValue = getVariantStockBySizeValue(listing);
  const variantStockValues = Object.values(stockBySizeValue).filter(value => value != null);
  const hasVariantStockData = variantStockValues.length > 0;

  if (hasVariantStockData) {
    if (normalizedSelectedSize) {
      return Object.prototype.hasOwnProperty.call(stockBySizeValue, normalizedSelectedSize)
        ? stockBySizeValue[normalizedSelectedSize]
        : 0;
    }

    return Math.max(...variantStockValues);
  }

  return getExplicitListingStockQuantity(listing);
};

export const hasPurchasableStockForListing = (listing, selectedSize = null) => {
  const stockQuantity = resolvePurchasableStockQuantity(listing, selectedSize);
  return stockQuantity == null ? true : stockQuantity > 0;
};

// Storefront/profile surfaces should hide listings unless we can resolve stock > 0.
export const hasVisibleStockForListing = (listing, selectedSize = null) => {
  const stockQuantity = resolvePurchasableStockQuantity(listing, selectedSize);
  return stockQuantity != null && stockQuantity > 0;
};

export const filterListingsWithVisibleStock = (listings, selectedSize = null) =>
  Array.isArray(listings)
    ? listings.filter(listing => hasVisibleStockForListing(listing, selectedSize))
    : [];
